import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { Redux } from 'src/store'
import { QueryParams } from '@/src/types/apps/queryParams'
import { restaurantAssignment } from '../resto365UserManagement'
import {
  RESTO365_ALL_FRANCHISE_CONTACT_LIST,
  RESTO365_ALL_FRANCHISE_LIST,
  RESTO365_FRANCHISE,
  RESTO365_FRANCHISE_COMMON_SEARCH,
  RESTO365_FRANCHISE_ERROR_RESET,
  RESTO365_GET_FRANCHISE_BY_ID,
  RESTO365_GET_FRANCHISE_CONTACT_BY_ID,
  RESTO365_GET_FRANCHISE_REVIEWS,
  RESTO365_CREATE_FRANCHISE_REVIEW,
  RESTO365_CREATE_FRANCHISE,
  RESTO365_GET_FRANCHISE_CONTACTS_BY_FRANCHISE_ID,
  RESTO365_CREATE_FRANCHISE_CONTACT,
  RESTO365_UPDATE_FRANCHISE_CONTACT,
  RESTO365_CHANGE_PRIMARY_FRANCHISE_CONTACT,
  RESTO365_CHECK_FRANCHISE_CONTACT_EMAIL,
  RESTO365_GET_FRANCHISE_REVIEWS_BY_ID,
  RESTO365_GET_FRANCHISE_REVIEW_DATA_BY_POS_STORE_ID,
  RESTO365_UPDATE_FRANCHISE_REVIEW,
  RESTO365_GET_FRANCHISE_REVIEW_QUESTIONS_BY_SECTION,
  RESTO365_CREATE_FRANCHISE_REVIEW_SECTION_RATINGS,
  RESTO365_GET_FRANCHISE_REVIEW_SECTION_RATINGS,
  RESTO365_GET_FRANCHISE_REVIEW_SECTION_RATING_BY_IDS,
  RESTO365_UPDATE_FRANCHISE_REVIEW_SECTION_RATING,
  RESTO365_SEARCH_FRANCHISES,
  RESTO365_GET_FRANCHISES_BY_IDS,
  RESTO365_IMPORT_FRANCHISE_REVIEW_DATA
} from './actions'
import { Gyg365FranchiseList, InitialFilter } from '@/src/types/apps/gyg365FranchiseListTypes'

interface InitialState {
  gyg365franchises: GetFranchisesListResponse | undefined
  franchiseData: Gyg365FranchiseList | undefined
  franchiseReviews:
    | {
        data: any[]
        meta: {
          totalRows: number
          pages: number
        }
        franchiseId?: number
      }
    | undefined
  get365franchisePending: boolean
  createReviewPending: boolean
  getReviewsPending: boolean
  searchPending: boolean
  importPending: boolean
  pending: boolean
  errors: any
  success?: string
  importResult?: {
    success: boolean
    totalRows?: number
    processedRows?: number
    insertedCount?: number
    updatedCount?: number
    validationErrors?: any[]
    duplicateErrors?: any[]
    processedRecords?: any[]
    message?: string
  }
  commonSearchData: InitialFilter
  franchiseContactsByFranchiseId?: {
    [franchiseId: number]: {
      data: any[]
      meta: {
        totalRows: number
        pages: number
      }
    }
  }
  franchiseReviewDataByRestaurantName?: any
  franchiseReviewQuestionsBySection?: any
  franchiseReviewSectionRatings?: any
}

interface GetFranchisesListResponse {
  data: any[]
  meta?: {
    totalRows: number
    pages: number
  }
}

type FranchiseResponse = {
  data: any
  meta: {
    totalRows: number
    pages: number
  }
}

interface InitialFilterResponse {
  data: InitialFilter
}

interface InitialFranchiseResponse {
  data: InitialState
}

interface GroupedResponse {
  posStoreId: number
  data: any[]
}

interface FranchiseReviewDataResponse {
  data: GroupedResponse[]
  meta: {
    totalRows: number
  }
}

const initialState: InitialState = {
  gyg365franchises: undefined,
  franchiseData: undefined,
  franchiseReviews: undefined,
  errors: null,
  success: undefined,
  pending: false,
  get365franchisePending: false,
  createReviewPending: false,
  getReviewsPending: false,
  searchPending: false,
  importPending: false,
  importResult: undefined,
  commonSearchData: {
    searchBar: ''
  },
  franchiseContactsByFranchiseId: {},
  franchiseReviewDataByRestaurantName: undefined
}

export const createFranchiseReview = createAsyncThunk<FranchiseResponse, Gyg365FranchiseList, Redux>(
  RESTO365_CREATE_FRANCHISE_REVIEW,
  async (reviewData, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient.post('resto365-franchise-reviews', reviewData)
      const data = await response.data

      return data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

export const gyg365FranchiseList = createAsyncThunk<GetFranchisesListResponse, QueryParams | undefined, Redux>(
  RESTO365_ALL_FRANCHISE_LIST,
  async (params = { page: 0, pageSize: 10 }, { extra, rejectWithValue }) => {
    try {
      const responseData = await extra.httpClient.get(`resto365-franchise`, {
        params: {
          page: (params.page ?? 0) + 1, // API expects 1-based pagination
          pageSize: params.pageSize ?? 10, // Changed from limit to pageSize to match backend
          sortBy: params.sortBy,
          ...(params.filters || {})
        }
      })

      // Return only serializable data, not the full response with headers
      return responseData.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
      throw error
    }
  }
)

export const get365FranchiseByIdData = createAsyncThunk<FranchiseResponse, number, Redux>(
  RESTO365_GET_FRANCHISE_BY_ID,
  async (Id: number, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient.get(`resto365-franchise/${Id}`)

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
      throw error
    }
  }
)

export const gyg365FranchiseContactList = createAsyncThunk<GetFranchisesListResponse, void, Redux>(
  RESTO365_ALL_FRANCHISE_CONTACT_LIST,
  async (_, { extra }) => {
    const responseData = await extra.httpClient.get(`resto365-franchise-contact`)

    // Return only serializable data, not the full response with headers
    return responseData.data
  }
)

export const get365FranchiseContactByIdData = createAsyncThunk<FranchiseResponse, number, Redux>(
  RESTO365_GET_FRANCHISE_CONTACT_BY_ID,
  async (Id: number, { extra }) => {
    const response = await extra.httpClient.get(`resto365-franchise-contact/${Id}`)

    return response.data
  }
)

export const commonSearchInitial = createAsyncThunk<InitialFilterResponse, void, Redux>(
  RESTO365_FRANCHISE_COMMON_SEARCH,
  async () => {
    return {
      data: initialState.commonSearchData
    }
  }
)

export const clearErrorData = createAsyncThunk<InitialFranchiseResponse, void, Redux>(
  RESTO365_FRANCHISE_ERROR_RESET,
  async () => {
    return {
      data: initialState
    }
  }
)

export const getFranchiseReviews = createAsyncThunk<FranchiseResponse, number, Redux>(
  RESTO365_GET_FRANCHISE_REVIEWS,
  async (franchiseId: number, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient.get(`resto365-franchise-reviews/franchise/${franchiseId}`)

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
      throw error
    }
  }
)

export const createFranchise = createAsyncThunk<any, { name: string }, Redux>(
  RESTO365_CREATE_FRANCHISE,
  async (payload, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient.post('resto365-franchise', payload)

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }

      return
    }
  }
)

export const getFranchiseContactsByFranchiseId = createAsyncThunk<any, number, Redux>(
  RESTO365_GET_FRANCHISE_CONTACTS_BY_FRANCHISE_ID,
  async (franchiseId, { extra }) => {
    const response = await extra.httpClient.get(`resto365-franchise-contact/franchise/${franchiseId}`)

    return response.data
  }
)

export const createFranchiseContact = createAsyncThunk<any, any, Redux>(
  RESTO365_CREATE_FRANCHISE_CONTACT,
  async (payload, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient.post('resto365-franchise-contact', payload)

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
      throw error
    }
  }
)

export const updateFranchiseContact = createAsyncThunk<any, { id: number; isPrimary: boolean }, Redux>(
  RESTO365_UPDATE_FRANCHISE_CONTACT,
  async ({ id, isPrimary }, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient.patch(`resto365-franchise-contact/${id}`, { isPrimary })

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
      throw error
    }
  }
)

export const changePrimaryFranchiseContact = createAsyncThunk<
  any,
  { franchiseContactId: number; franchiseId: number; isPrimary: boolean },
  Redux
>(
  RESTO365_CHANGE_PRIMARY_FRANCHISE_CONTACT,
  async ({ franchiseContactId, franchiseId, isPrimary }, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient.patch('resto365-franchise-contact/change-primary', {
        franchiseContactId: Number(franchiseContactId),
        franchiseId: Number(franchiseId),
        isPrimary
      })

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
      throw error
    }
  }
)

export const checkFranchiseContactEmail = createAsyncThunk<any, string, Redux>(
  RESTO365_CHECK_FRANCHISE_CONTACT_EMAIL,
  async (email, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient.get(`resto365-franchise-contact/email/${encodeURIComponent(email)}`)

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
      throw error
    }
  }
)

export const getFranchiseReviewById = createAsyncThunk<FranchiseResponse, number, Redux>(
  RESTO365_GET_FRANCHISE_REVIEWS_BY_ID,
  async (reviewId: number, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient.get(`resto365-franchise-reviews/${reviewId}`)

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
      throw error
    }
  }
)

export const getFranchiseReviewDataByPosStoreId = createAsyncThunk<
  FranchiseReviewDataResponse,
  {
    posStoreIds?: number[]
    fiscalYear?: string
    month?: string
    sortBy?: string
  },
  Redux
>(RESTO365_GET_FRANCHISE_REVIEW_DATA_BY_POS_STORE_ID, async (params, { extra }) => {
  const queryParams = new URLSearchParams()

  if (params.posStoreIds?.length) {
    queryParams.append('posStoreIds', params.posStoreIds.join(','))
  }
  if (params.fiscalYear) {
    queryParams.append('fiscalYear', params.fiscalYear)
  }
  if (params.month) {
    queryParams.append('month', params.month)
  }
  if (params.sortBy) {
    queryParams.append('sortBy', params.sortBy)
  }

  const response = await extra.httpClient.get(`resto365-franchise-review-data?${queryParams.toString()}`)

  return response.data
})

export const updateFranchiseReview = createAsyncThunk<any, any, Redux>(
  RESTO365_UPDATE_FRANCHISE_REVIEW,
  async (payload, { extra }) => {
    const { id, ...updateData } = payload
    const response = await extra.httpClient.patch(`resto365-franchise-reviews/${id}`, updateData)

    return response.data
  }
)

export const getFranchiseReviewQuestionsBySection = createAsyncThunk<any, any, Redux>(
  RESTO365_GET_FRANCHISE_REVIEW_QUESTIONS_BY_SECTION,
  async (payload, { extra }) => {
    const response = await extra.httpClient.get(
      `resto365-franchise-review-section-questions/section/${payload.section}`
    )

    return response.data
  }
)

export const createFranchiseReviewSectionRatings = createAsyncThunk<any, any, Redux>(
  RESTO365_CREATE_FRANCHISE_REVIEW_SECTION_RATINGS,
  async (payload, { extra }) => {
    const response = await extra.httpClient.post(`resto365-franchise-review-section-ratings`, payload)

    return response.data
  }
)

export const getFranchiseReviewSectionRatings = createAsyncThunk<any, { reviewId: number; section: string }, Redux>(
  RESTO365_GET_FRANCHISE_REVIEW_SECTION_RATINGS,
  async (payload, { extra }) => {
    const response = await extra.httpClient.get(
      `resto365-franchise-review-section-ratings/review/${payload.reviewId}/section/${payload.section}`
    )

    return response.data
  }
)

export const getFranchiseReviewSectionRatingByIds = createAsyncThunk<
  any,
  { reviewId: number; questionId: number; section: string },
  Redux
>(RESTO365_GET_FRANCHISE_REVIEW_SECTION_RATING_BY_IDS, async (payload, { extra }) => {
  const response = await extra.httpClient.get(
    `resto365-franchise-review-section-ratings/review/${payload.reviewId}/question/${payload.questionId}/section/${payload.section}`
  )

  return response.data
})

export const updateFranchiseReviewSectionRating = createAsyncThunk<any, { id: number; payload: any }, Redux>(
  RESTO365_UPDATE_FRANCHISE_REVIEW_SECTION_RATING,
  async ({ id, payload }, { extra }) => {
    const response = await extra.httpClient.patch(`resto365-franchise-review-section-ratings/${id}`, payload)

    return response.data
  }
)

export const searchFranchises = createAsyncThunk<GetFranchisesListResponse, QueryParams & { q: string }, Redux>(
  RESTO365_SEARCH_FRANCHISES,
  async (params, { extra, rejectWithValue }) => {
    try {
      const responseData = await extra.httpClient.get(`resto365-franchise/search`, {
        params: {
          q: params.q,
          page: (params.page ?? 0) + 1, // API expects 1-based pagination
          pageSize: params.pageSize ?? 10,
          sortBy: params.sortBy,
          ...(params.filters || {})
        }
      })

      // Return only serializable data, not the full response with headers
      return responseData.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
      throw error
    }
  }
)

export const getFranchisesByIds = createAsyncThunk<GetFranchisesListResponse, number[], Redux>(
  RESTO365_GET_FRANCHISES_BY_IDS,
  async (franchiseIds, { extra, rejectWithValue }) => {
    try {
      const idsParam = franchiseIds.join(',')
      const responseData = await extra.httpClient.get(`resto365-franchise/by-ids?ids=${idsParam}`)

      // The by-ids API returns an array directly, so we need to wrap it in the expected format
      return {
        data: responseData.data || responseData,
        meta: {
          totalRows: Array.isArray(responseData.data || responseData) ? (responseData.data || responseData).length : 0,
          pages: 1
        }
      }
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
      throw error
    }
  }
)

interface ImportFranchiseReviewDataResponse {
  data: {
    success: boolean
    totalRows?: number
    processedRows?: number
    insertedCount?: number
    updatedCount?: number
    validationErrors?: any[]
    duplicateErrors?: any[]
    processedRecords?: any[]
    message?: string
  }
}

export const importFranchiseReviewData = createAsyncThunk<ImportFranchiseReviewDataResponse, File, Redux>(
  RESTO365_IMPORT_FRANCHISE_REVIEW_DATA,
  async (file, { extra, rejectWithValue }) => {
    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await extra.httpClient.post('resto365-franchise-review-data-import/import-csv', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      // Handle 200 responses with success: false (validation errors, duplicates, etc.)
      if (response.data?.data && response.data.data.success === false) {
        return response.data
      }

      return response.data
    } catch (error: any) {
      if (error.response) {
        // Handle 400/500 errors with top-level message and error fields
        const errorData = error.response.data

        return rejectWithValue(errorData)
      }
      throw error
    }
  }
)

export const gyg365FranchiseSlice = createSlice({
  name: RESTO365_FRANCHISE,
  initialState,
  reducers: {
    // Clear franchise contacts cache for a specific franchise
    clearFranchiseContactsCache: (state, action: { payload: number }) => {
      if (state.franchiseContactsByFranchiseId?.[action.payload]) {
        delete state.franchiseContactsByFranchiseId[action.payload]
      }
    },

    // Clear all franchise contacts cache
    clearAllFranchiseContactsCache: state => {
      state.franchiseContactsByFranchiseId = {}
    }
  },
  extraReducers: builder => {
    builder

      // Create Reviews
      .addCase(createFranchiseReview.fulfilled, (state, action) => {
        state.franchiseData = action.payload.data
        state.errors = null
        state.createReviewPending = false
      })
      .addCase(createFranchiseReview.pending, state => {
        state.createReviewPending = true
        state.success = undefined
      })
      .addCase(createFranchiseReview.rejected, (state, action) => {
        let errors
        if (action.payload) {
          errors = action.payload
        } else {
          errors = action.error
        }
        state.errors = errors
        state.success = undefined
        state.createReviewPending = false
      })

      .addCase(gyg365FranchiseList.fulfilled, (state, action) => {
        // action.payload is the full response object { data: [...], meta: {...} }
        state.gyg365franchises = action.payload
        state.franchiseData = action.payload?.data?.[0] || undefined
        state.success = undefined
        state.get365franchisePending = false
        state.errors = null
      })
      .addCase(gyg365FranchiseList.pending, state => {
        state.success = undefined
        state.franchiseData = undefined
        state.get365franchisePending = true
      })
      .addCase(gyg365FranchiseList.rejected, (state, action) => {
        let errors
        if (action.payload) {
          errors = action.payload
        } else {
          errors = action.error
        }
        state.errors = errors
        state.get365franchisePending = false
        state.success = undefined
      })

      .addCase(gyg365FranchiseContactList.fulfilled, state => {
        // gyg365FranchiseContactList returns a list of contacts, not a single franchise
        // Don't update franchiseData as it expects a single Gyg365FranchiseList object
        state.success = undefined
        state.get365franchisePending = false
        state.errors = null
      })
      .addCase(gyg365FranchiseContactList.pending, state => {
        state.success = undefined
        state.franchiseData = undefined
        state.get365franchisePending = true
      })
      .addCase(gyg365FranchiseContactList.rejected, state => {
        state.get365franchisePending = false
        state.success = undefined
      })

      .addCase(get365FranchiseByIdData.fulfilled, (state, action) => {
        state.franchiseData = action.payload.data
        state.success = undefined
        state.get365franchisePending = false
        state.errors = null
      })
      .addCase(get365FranchiseByIdData.pending, state => {
        state.success = undefined
        if (!state.franchiseData) {
          state.franchiseData = undefined
        }
        state.get365franchisePending = true
      })
      .addCase(get365FranchiseByIdData.rejected, (state, action) => {
        let errors
        if (action.payload) {
          errors = action.payload
        } else {
          errors = action.error
        }
        state.errors = errors
        state.get365franchisePending = false
        state.success = undefined
        if (!state.franchiseData?.id) {
          state.franchiseData = undefined
        }
      })

      .addCase(get365FranchiseContactByIdData.fulfilled, (state, action) => {
        state.franchiseData = action.payload.data
        state.success = undefined
        state.get365franchisePending = false
        state.errors = null
      })
      .addCase(get365FranchiseContactByIdData.pending, state => {
        state.success = undefined
        state.franchiseData = undefined
        state.get365franchisePending = true
      })
      .addCase(get365FranchiseContactByIdData.rejected, state => {
        state.get365franchisePending = false
        state.success = undefined
      })

      .addCase(commonSearchInitial.fulfilled, state => {
        state.commonSearchData = {
          searchBar: ''
        }
      })

      .addCase(clearErrorData.fulfilled, state => {
        state.success = undefined
        state.pending = false
        state.get365franchisePending = false
        state.errors = null
      })

      .addCase(getFranchiseReviews.fulfilled, (state, action) => {
        state.franchiseReviews = {
          ...action.payload,
          franchiseId: action.meta.arg
        }
        state.getReviewsPending = false
        state.errors = null
      })
      .addCase(getFranchiseReviews.pending, (state, action) => {
        state.getReviewsPending = true

        // Only clear if we're fetching for a different franchise
        if (state.franchiseReviews?.franchiseId !== action.meta.arg) {
          state.franchiseReviews = undefined
        }
      })
      .addCase(getFranchiseReviews.rejected, (state, action) => {
        let errors
        if (action.payload) {
          errors = action.payload
        } else {
          errors = action.error
        }
        state.errors = errors
        state.getReviewsPending = false

        // Only clear on error if we're sure it's a different request
        if (state.franchiseReviews?.franchiseId !== action.meta.arg) {
          state.franchiseReviews = undefined
        }
      })

      .addCase(createFranchise.pending, state => {
        state.pending = true
        state.success = undefined
      })
      .addCase(createFranchise.fulfilled, state => {
        state.pending = false
        state.success = 'Franchise created successfully'
        state.errors = null
      })
      .addCase(createFranchise.rejected, (state, action) => {
        let errors
        if (action.payload) {
          errors = action.payload
        } else {
          errors = action.error
        }
        state.errors = errors
        state.success = undefined
        state.pending = false
      })

      .addCase(getFranchiseContactsByFranchiseId.fulfilled, (state, action) => {
        if (action.meta.arg) {
          const contacts = Array.isArray(action.payload.data) ? action.payload.data : []
          state.franchiseContactsByFranchiseId = {
            ...state.franchiseContactsByFranchiseId,
            [action.meta.arg]: {
              data: contacts,
              meta: action.payload.meta || { totalRows: contacts.length, pages: 1 }
            }
          }
        }
      })
      .addCase(getFranchiseContactsByFranchiseId.pending, () => {
        // Don't clear existing data to prevent infinite loops
        // The data will be updated in fulfilled state
      })
      .addCase(getFranchiseContactsByFranchiseId.rejected, (state, action) => {
        // Only clear the specific franchise data on error if it's a critical error
        if (
          action.meta.arg &&
          state.franchiseContactsByFranchiseId &&
          state.franchiseContactsByFranchiseId[action.meta.arg] &&
          action.error?.message?.includes('404')
        ) {
          delete state.franchiseContactsByFranchiseId[action.meta.arg]
        }
      })

      .addCase(createFranchiseContact.pending, state => {
        state.pending = true
        state.success = undefined
      })
      .addCase(createFranchiseContact.fulfilled, (state, action) => {
        state.pending = false
        state.success = 'Franchise contact created successfully'
        state.errors = null

        // Invalidate franchise contacts cache for the affected franchise
        if (action.payload?.data?.franchiseId) {
          const franchiseId = action.payload.data.franchiseId
          if (state.franchiseContactsByFranchiseId?.[franchiseId]) {
            delete state.franchiseContactsByFranchiseId[franchiseId]
          }
        }

        // Also invalidate franchiseData if it matches
        if (state.franchiseData?.id === action.payload?.data?.franchiseId) {
          state.franchiseData = undefined
        }
      })
      .addCase(createFranchiseContact.rejected, (state, action) => {
        let errors
        if (action.payload) {
          errors = action.payload
        } else {
          errors = action.error
        }
        state.errors = errors
        state.success = undefined
        state.pending = false
      })

      // Invalidate franchise contacts cache when restaurant assignment changes
      // This handles linking/unlinking franchisees to restaurants
      .addCase(restaurantAssignment.fulfilled, state => {
        // Clear all franchise contacts cache since restaurant assignment affects franchise contacts
        // The cache will be refetched when the franchise page loads
        state.franchiseContactsByFranchiseId = {}

        // Also invalidate franchiseData to force refetch of franchise details including contacts
        if (state.franchiseData) {
          state.franchiseData = undefined
        }
      })

      .addCase(getFranchiseReviewDataByPosStoreId.fulfilled, (state, action) => {
        state.franchiseReviewDataByRestaurantName = action.payload
      })
      .addCase(getFranchiseReviewDataByPosStoreId.pending, () => {
        // Temporarily disable clearing to prevent infinite loops
        // The data will be updated in fulfilled state
      })
      .addCase(getFranchiseReviewDataByPosStoreId.rejected, () => {
        // Temporarily disable clearing to prevent infinite loops
        // Keep existing data on error
      })

      .addCase(getFranchiseReviewById.fulfilled, state => {
        // Don't overwrite franchiseData with review data
        // franchiseData should only contain franchise information
        state.errors = null
      })
      .addCase(getFranchiseReviewById.pending, state => {
        state.errors = null
      })
      .addCase(getFranchiseReviewById.rejected, (state, action) => {
        let errors
        if (action.payload) {
          errors = action.payload
        } else {
          errors = action.error
        }
        state.errors = errors
      })

      .addCase(updateFranchiseReview.fulfilled, () => {
        // Don't overwrite franchiseReviewDataByRestaurantName with review update response
        // The review update response only contains review data, not restaurant performance data
        // We need to preserve the existing restaurant performance data
        // state.franchiseReviewDataByRestaurantName = action.payload
      })
      .addCase(updateFranchiseReview.pending, () => {
        // Don't clear data in pending state to prevent infinite loops
        // The data will be updated in fulfilled state
      })
      .addCase(updateFranchiseReview.rejected, () => {
        // Don't clear data in rejected state to prevent infinite loops
        // Keep existing data on error
      })

      .addCase(getFranchiseReviewQuestionsBySection.fulfilled, (state, action) => {
        state.franchiseReviewQuestionsBySection = action.payload
      })
      .addCase(getFranchiseReviewQuestionsBySection.pending, state => {
        state.franchiseReviewQuestionsBySection = undefined
      })
      .addCase(getFranchiseReviewQuestionsBySection.rejected, state => {
        state.franchiseReviewQuestionsBySection = undefined
      })

      .addCase(createFranchiseReviewSectionRatings.fulfilled, (state, action) => {
        state.franchiseReviewSectionRatings = action.payload
      })
      .addCase(createFranchiseReviewSectionRatings.pending, state => {
        state.franchiseReviewSectionRatings = undefined
      })
      .addCase(createFranchiseReviewSectionRatings.rejected, state => {
        state.franchiseReviewSectionRatings = undefined
      })

      .addCase(getFranchiseReviewSectionRatings.fulfilled, (state, action) => {
        state.franchiseReviewSectionRatings = action.payload
      })
      .addCase(getFranchiseReviewSectionRatings.pending, state => {
        state.franchiseReviewSectionRatings = undefined
      })
      .addCase(getFranchiseReviewSectionRatings.rejected, state => {
        state.franchiseReviewSectionRatings = undefined
      })

      .addCase(getFranchiseReviewSectionRatingByIds.fulfilled, (state, action) => {
        state.franchiseReviewSectionRatings = action.payload
      })
      .addCase(getFranchiseReviewSectionRatingByIds.pending, state => {
        state.franchiseReviewSectionRatings = undefined
      })
      .addCase(getFranchiseReviewSectionRatingByIds.rejected, state => {
        state.franchiseReviewSectionRatings = undefined
      })

      .addCase(updateFranchiseReviewSectionRating.fulfilled, (state, action) => {
        state.franchiseReviewSectionRatings = action.payload
      })
      .addCase(updateFranchiseReviewSectionRating.pending, state => {
        state.franchiseReviewSectionRatings = undefined
      })
      .addCase(updateFranchiseReviewSectionRating.rejected, state => {
        state.franchiseReviewSectionRatings = undefined
      })

      .addCase(searchFranchises.fulfilled, (state, action) => {
        // action.payload is the full response object { data: [...], meta: {...} }
        state.gyg365franchises = action.payload
        state.franchiseData = action.payload?.data?.[0] || undefined
        state.success = undefined
        state.searchPending = false
        state.errors = null
      })
      .addCase(searchFranchises.pending, state => {
        state.success = undefined
        state.franchiseData = undefined
        state.searchPending = true
      })
      .addCase(searchFranchises.rejected, (state, action) => {
        let errors
        if (action.payload) {
          errors = action.payload
        } else {
          errors = action.error
        }
        state.errors = errors
        state.searchPending = false
        state.success = undefined
      })

      .addCase(getFranchisesByIds.fulfilled, (state, action) => {
        // action.payload is the full response object { data: [...], meta: {...} }
        state.gyg365franchises = action.payload
        state.franchiseData = action.payload?.data?.[0] || undefined
        state.success = undefined
        state.searchPending = false
        state.errors = null
      })
      .addCase(getFranchisesByIds.pending, state => {
        state.success = undefined
        state.franchiseData = undefined
        state.searchPending = true
      })
      .addCase(getFranchisesByIds.rejected, (state, action) => {
        let errors
        if (action.payload) {
          errors = action.payload
        } else {
          errors = action.error
        }
        state.errors = errors
        state.searchPending = false
        state.success = undefined
      })

      .addCase(importFranchiseReviewData.fulfilled, (state, action) => {
        // Handle both success and failure cases (200 responses with success: false)
        if (action.payload?.data) {
          state.importResult = action.payload.data
        } else {
          state.importResult = action.payload as any
        }
        state.importPending = false
        state.errors = null
      })
      .addCase(importFranchiseReviewData.pending, state => {
        state.importPending = true
        state.importResult = undefined
        state.errors = null
      })
      .addCase(importFranchiseReviewData.rejected, (state, action) => {
        state.importPending = false
        let errors
        if (action.payload) {
          errors = action.payload
          const payload = action.payload as any

          // Handle 400/500 errors with top-level message (statusCode, message, error)
          if (payload?.statusCode && payload?.message) {
            state.importResult = {
              success: false,
              validationErrors: [],
              duplicateErrors: [],
              message: payload.message || 'Failed to import data. Please try again.'
            }
          }

          // If the error response contains data with validation/duplicate errors, set importResult
          else if (payload?.data) {
            state.importResult = {
              success: false,
              validationErrors: payload.data.validationErrors || [],
              duplicateErrors: payload.data.duplicateErrors || [],
              message: payload.data.message || 'Failed to import data. Please try again.'
            }
          }

          // Fallback: create importResult from the error message
          else if (payload?.message) {
            state.importResult = {
              success: false,
              validationErrors: [],
              duplicateErrors: [],
              message: payload.message
            }
          }
        } else {
          errors = action.error
          state.importResult = {
            success: false,
            validationErrors: [],
            duplicateErrors: [],
            message: 'Failed to import data. Please try again.'
          }
        }
        state.errors = errors
      })
  }
})

export default gyg365FranchiseSlice.reducer
